import { useLocalStorage } from "PosterEditor/hooks/use-locale-storage";
import { useState, useEffect } from "react";
import { PosterProject, ProjectStore } from "PosterEditor/model/poster-project";
import { v4 as uuid } from "uuid";
import { defaultProject } from "PosterEditor/model/default-project-style";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { applyTemplateToProject } from "PosterEditor/model/project-template";
import { useAuth } from "BEDROCK/auth/supabase-auth";

export const useProjectLocalStore = () => {
  const { isUserAuthenticated } = useAuth();
  const { save, read, remove } = useLocalStorage();
  const [projects, setProjects] = useState<PosterProject[]>([]);
  const [, forceUpdate] = useState(0);

  const createProject = () => {
    const project = { ...defaultProject, id: uuid() };
    saveProject(project);
    return project;
  };

  const createDraft = (template: ProjectTemplate | undefined) => {
    const project = {
      ...defaultProject,
      id: isUserAuthenticated ? uuid() : "guest_project",
      isTemplateDraft: true,
    };
    if (template) {
      applyTemplateToProject(template, project);
    }
    saveProject(project);
    return project;
  };

  const draftProject = (
    createNew: boolean,
    template: ProjectTemplate | undefined
  ) => {
    if (createNew) {
      deleteDraft();
      return createDraft(template);
    }
    const draft = projects.find((p) => p.isTemplateDraft);
    if (!draft) {
      return createDraft(template);
    }
    return draft;
  };

  const deleteDraft = () => {
    const projects = loadProjects() || [];
    const draftIndex = projects.findIndex((p) => p.isTemplateDraft);
    if (draftIndex === -1) return;
    const updatedProjects = projects.filter((p) => !p.isTemplateDraft);
    save("projectStore", updatedProjects);
    setProjects(updatedProjects);
  };

  const deleteProject = (project: PosterProject) => {
    const prevProjects = loadProjects();
    const newProjects = prevProjects ? [...prevProjects] : [];
    const index = newProjects.findIndex((p) => p.id === project.id);
    let updatedProjects;
    if (index !== -1) {
      updatedProjects = newProjects.filter((p) => p.id !== project.id);
    } else {
      updatedProjects = [...newProjects];
    }
    save("projectStore", updatedProjects);
    setProjects(updatedProjects);
    return updatedProjects;
  };

  const deleteAllProjects = () => {
    setProjects([]);
    remove("projectStore");
  };

  const saveProject = (project: PosterProject) => {
    const projects = loadProjects();
    if (!projects) {
      setProjects([project]);
      save("projectStore", [project]);
      return;
    }
    const newProjects = projects;
    const index = projects.findIndex((p) => p.id === project.id);

    let updatedProjects;
    if (index !== -1) {
      updatedProjects = newProjects.map((p) =>
        p.id === project.id ? project : p
      );
    } else {
      updatedProjects = [{ ...project }, ...newProjects];
    }
    save("projectStore", updatedProjects);
    setProjects(updatedProjects);
    return updatedProjects;
  };

  const loadProjects = (includeDrafts = false) => {
    const projects = read("projectStore") as PosterProject[] | undefined;
    if (!projects) {
      setProjects([]);
      return [];
    }

    const filteredProjects = includeDrafts
      ? projects
      : projects.filter((p) => !p.isTemplateDraft);
    setProjects(filteredProjects);
    return filteredProjects;
  };

  const loadProject = (id: string) => {
    const projects = read("projectStore") as PosterProject[] | undefined;
    return projects?.filter((project) => project.id === id)[0];
  };

  return {
    loadProjects,
    loadProject,
    saveProject,
    createProject,
    deleteProject,
    deleteAllProjects,
    draftProject,
    deleteDraft,
  };
};
